import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Card } from "react-bootstrap";
import { Spinner, Filter } from "../../Common";
import DataLoader from "../../Common/DataLoader/DataLoader";
import styles from "./mcb.module.css";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import DataTable from "./../../Common/DataTable/DataTable";
import Tab from "./../../Common/Tab/Tab";
import Detail from "./Detail";
import Beneficiary from "./Beneficiary";
import Transaction from "./Transaction";
import LienTransaction from "./LienTransaction.js";
import Transfers from "./LienBeneficiariesTransfers.js";
import AccountDetails from "./AccountDetails";
import LienAccountDetails from "./LienAccountDetails.js";
import UnlienAccountDetails from "./UnlienAccountDetails.js";
import Document from "./Document.js";
import SubscriptionAndPlan from "./SubscriptionAndPlan";
import {
  getMcbUsers,
  getMcbUsersDetails,
  closeMcbAccount,
  getUsersPartnerList,
} from "../../api";
import singleLeftSvg from "../../public/images/singleLeft.svg";
import BasicDetails from "./BasicDetails";
import SearchFilterBar from "../../Common/SearchFilter/SearchFilterBar.js";
import { MCBUserColumn } from "../../utils/tableHeader.js";
import { MCBkeyToParamMap } from "../../utils/routeKeyParam.js";
import { buildFilterParams, productType } from "../../utils/utils.js";
import Result from "../../Common/V2/Widgets/Result/index.js";
import NoDataFound from "../../Common/V2/images/noDataFound.js";
import withFeatureActions from "../../withFeatureActions";
import { useRoleFeature } from "../../context/RoleFeatureContext";
import CommentModal from "../../Common/CommentModal/commentModal.js";
import { addDays } from "date-fns";

const BasicDetailsWithAccess = withFeatureActions(
  BasicDetails,
  "MCB",
  "Details",
);
const AccountDetailsWithAccess = withFeatureActions(
  AccountDetails,
  "MCB",
  "AccountDetails",
);
const LienAccountDetailsWithAccess = withFeatureActions(
  LienAccountDetails,
  "MCB",
  "LienAccountDetails",
);
const UnlienAccountDetailsWithAccess = withFeatureActions(
  UnlienAccountDetails,
  "MCB",
  "UnlienAccountDetails",
);
const SubscriptionAndPlanWithAccess = withFeatureActions(
  SubscriptionAndPlan,
  "MCB",
  "SubscriptionAndPlan",
);
const BeneficiaryWithAccess = withFeatureActions(
  Beneficiary,
  "MCB",
  "Beneficiaries",
);
const TransactionWithAccess = withFeatureActions(
  Transaction,
  "MCB",
  "Transactions",
);
const LienTransactionWithAccess = withFeatureActions(
  LienTransaction,
  "MCB",
  "LienTransactions",
);
const DocumentsWithAccess = withFeatureActions(Document, "MCB", "Documents");
const TransfersWithAccess = withFeatureActions(Transfers, "MCB", "Transfers");

const Mcb = ({ featureAccess }) => {
  const { readAccess, writeAccess, modifyAccess } = featureAccess;
  const [list, setList] = useState([]);
  const [key, setKey] = useState("");
  const [filteredList, setFilteredList] = useState([]);
  const [showDetail, setShowDetail] = useState(false);
  const [showTable, setShowTable] = useState(true);
  const [dataLoading, setDataLoading] = useState(true);
  const [inputText, setInputText] = useState("");
  const [userDetails, setUserDetails] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [detail, setDetail] = useState();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [showFilter, setShowFilter] = useState(false);
  const date = new Date();
  const [startDate, setStartDate] = useState(addDays(date, -15));
  const [endDate, setEndDate] = useState(date);
  const [mcbStatus, setMcbStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeSearchText, setActiveSearchText] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [comment, setComment] = useState("");
  const [isComment, setIsComment] = useState("");
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { roleFeatureList } = useRoleFeature();
  const { id } = useParams();
  const firstFetch = useRef(true);
  const initialLoad = useRef(true);
  const [partnerList, setPartnerList] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState("");

  const tabAccessMap = {
    Details: "Details",
    AccountDetails: "Account Details",
    LienAccountDetails: "Lien Account Details",
    UnlienAccountDetails: "Unlien Account Details",
    SubscriptionAndPlan: "SubscriptionAndPlan",
    Beneficiaries: "Beneficiaries",
    Transactions: "Transactions",
    LienTransactions: "Lien Transactions",
    Documents: "Documents",
    Transfers: "Transfers",
  };

  const visibleTabs = Object.keys(tabAccessMap).filter((tabKey) => {
    const sourceFeatures = roleFeatureList?.features?.find(
      (featureGroup) => featureGroup.source === "MCB",
    );
    const matchedFeature =
      sourceFeatures?.featureList?.find(
        (f) => f.componentName === tabAccessMap[tabKey],
      ) ||
      sourceFeatures?.featureList
        ?.flatMap((f) => f.subFeatures || [])
        ?.find((sf) => sf.componentName === tabAccessMap[tabKey]);

    if (!matchedFeature?.readAccess) return false;

    if (
      tabKey === "LienAccountDetails" ||
      tabKey === "UnlienAccountDetails" ||
      tabKey === "LienTransactions" ||
      tabKey === "Transfers"
    ) {
      if (detail?.profileType !== "STUDENT_LIEN") {
        return false;
      }
    }

    return true;
  });

  const getDefaultFilter = () => {
    const today = new Date();
    return buildFilterParams("", addDays(today, -15), today, "");
  };

  const fetchUserDetails = async (userId) => {
    try {
      const response = await getMcbUsersDetails(userId);
      if (response && response.data && response.data.data) {
        setDetail(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const fetchMcbPartners = async () => {
    const resp = await getUsersPartnerList({ appName: productType.MCB });
    const partners = resp?.data?.data?.map((item) => item?.partnerCd);
    setPartnerList([...partners]);
  };

  useEffect(() => {
    const tabParam = searchParams.get("tab");

    if (id && tabParam) {
      setUserDetails(id);
      setShowDetail(true);
      setShowTable(false);

      const selectedKey = Object.keys(MCBkeyToParamMap).find(
        (k) => MCBkeyToParamMap[k] === tabParam,
      );
      if (selectedKey) setKey(selectedKey);
      if (initialLoad.current) {
        fetchUserDetails(id);
        initialLoad.current = false;
      }
    }
  }, [id, searchParams]);

  const fetchUsers = async (page = 1, filter = "", searchTerm = "") => {
    setSearchLoading(true);
    setDataLoading(true);
    try {
      const encodedSearchTerm = searchTerm
        ? encodeURIComponent(searchTerm.trim())
        : "";
      const response = await getMcbUsers(encodedSearchTerm, page, pageSize, filter);
      const users = response?.data?.data?.users;
      const count = response?.data?.data?.count || 0;
      setFilteredList(users);
      setList(users);
      setTotalPages(Math.ceil(count / pageSize));
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setSearchLoading(false);
      setDataLoading(false);
    }
  };

  useEffect(() => {
    if (firstFetch.current && !id) {
      const defaultFilter = getDefaultFilter();
      setActiveFilter(defaultFilter);
      fetchUsers(1, defaultFilter, "");
      fetchMcbPartners();
      firstFetch.current = false;
    }
  }, [id]);

  useEffect(() => {
    if (key !== "") {
      const urlId = id;
      const tab = MCBkeyToParamMap[key];
      setSearchParams({ id: urlId, tab });
    }
  }, [key]);

  useEffect(() => {
    if (key === "Details" && userDetails) {
      fetchUserDetails(userDetails);
    }
  }, [key]);

  useEffect(() => {
    if (firstFetch.current) return;

    const debounceTimeout = setTimeout(() => {
      if (inputText.trim()) {
        setCurrentPage(1);
        setActiveSearchText(inputText.trim());
        setActiveFilter("");
        fetchUsers(1, "", inputText.trim());
      } else if (activeSearchText) {
        setActiveSearchText("");
        const defaultFilter = getDefaultFilter();
        setActiveFilter(defaultFilter);
        setCurrentPage(1);
        fetchUsers(1, defaultFilter, "");
      }
    }, 500);
    return () => clearTimeout(debounceTimeout);
  }, [inputText]);

  const handleClickRow = (data) => {
    const url = `/mcb/${data?.userId}?tab=${MCBkeyToParamMap["Details"]}`;
    window.open(url, "_blank");
  };

  const onSubmitFilter = () => {
    if (!startDate || !endDate) {
      setErrorMessage("Please select dates.");
      return;
    }

    setErrorMessage("");
    setCurrentPage(1);

    const filterParams = buildFilterParams(
      mcbStatus,
      startDate,
      endDate,
      selectedPartner,
    );
    setActiveFilter(filterParams);
    setActiveSearchText("");

    fetchUsers(1, filterParams, "");
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
  };
  useEffect(() => {
    if (firstFetch.current) return;

    const date = new Date();
    setStartDate(addDays(date, -15));
    setEndDate(date);
    setMcbStatus("");
    setSelectedPartner("");
    setErrorMessage("");
    setInputText("");
    setActiveSearchText("");

    if (!showFilter) {
      const defaultFilter = getDefaultFilter();
      setActiveFilter(defaultFilter);
      setCurrentPage(1);
      fetchUsers(1, defaultFilter, "");
    } else {
      setCurrentPage(1);
    }
  }, [showFilter]);

  useEffect(() => {
    if (firstFetch.current) return;
    if (!activeFilter && !activeSearchText) return;
    fetchUsers(currentPage, activeFilter, activeSearchText);
  }, [currentPage]);

  const handleBack = () => {
    setUserDetails("");
    setShowDetail(false);
    setShowTable(true);
    setSearchParams();
    navigate("/mcb");
  };
  const handleCloseAccount = (user) => {
    setSelectedUserId(user?.userId);
    setIsCommentModalOpen(true);
  };
  const handleConfirmCloseAccount = async () => {
    setLoading(true);
    try {
      const res = await closeMcbAccount({
        userId: selectedUserId,
        comment: comment.trim(),
      });

      if (res?.status === 200) {
        setSuccessMessage(res?.data || "Account closed successfully.");
        setTimeout(() => {
          setIsCommentModalOpen(false);
          setComment("");
          setSelectedUserId(null);
          setSuccessMessage("");
          fetchUsers(
            currentPage,
            activeFilter,
            activeSearchText ? encodeURIComponent(activeSearchText) : "",
          );
        }, 3000);
      } else {
        setErrorMessage(res?.data?.message || "Failed to close account.");
      }
    } catch (err) {
      setErrorMessage("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {showTable && (
        <>
          <SearchFilterBar
            inputText={inputText}
            handleInputChange={handleInputChange}
            setShowFilter={(value) => {
              if (value === true && activeSearchText) {
                setList([]);
                setFilteredList([]);
                setDataLoading(true);
              }
              setShowFilter(value);
            }}
            showFilter={showFilter}
            setInputText={setInputText}
            placeholder="Search by Email, Username, Phone no, UserId, Global Account no."
          />
          {showFilter && (
            <Card
              className={`${styles.customCard} ${isCommentModalOpen ? styles.modalOpen : ""}`}
            >
              <Card.Body className={styles.customCardBody}>
                <Filter
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  onSearch={onSubmitFilter}
                  loading={searchLoading}
                  errorMessage={errorMessage}
                  setErrorMessage={setErrorMessage}
                  mcbStatus={mcbStatus}
                  setMcbStatus={setMcbStatus}
                  showMcbStatus={true}
                  partner={selectedPartner}
                  setPartner={setSelectedPartner}
                  partnerList={partnerList}
                  showPartner={true}
                  showNextStep={false}
                />
              </Card.Body>
            </Card>
          )}
          <DataLoader isLoading={dataLoading}>
            {filteredList?.length ? (
              <DataTable
                columns={MCBUserColumn(featureAccess)}
                list={list}
                handleClickRow={handleClickRow}
                rowClick={true}
                totalPages={totalPages}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                pagination={true}
                handleCloseAccount={modifyAccess ? handleCloseAccount : null}
                title="Users"
                enableInfiniteScroll={false}
              />
            ) : (
              <div className={styles.resultContainer}>
                <Result
                  icon={<NoDataFound width={80} height={80} />}
                  title="No Data Found"
                  subtitle="Your Data will be displayed here"
                />
              </div>
            )}
          </DataLoader>
        </>
      )}
      {showDetail && (
        <div>
          {detail && <Detail userDetails={detail} />}
          <Tab keys={visibleTabs} activeKey={key} onSelect={setKey} />

          {key === "Details" && <BasicDetailsWithAccess userDetails={detail} />}
          {key === "AccountDetails" && (
            <AccountDetailsWithAccess userId={userDetails} />
          )}
          {key === "LienAccountDetails" && (
            <LienAccountDetailsWithAccess userId={userDetails} detail={detail} />
          )}
          {key === "UnlienAccountDetails" && (
            <UnlienAccountDetailsWithAccess userId={userDetails} detail={detail} />
          )}
          {key === "SubscriptionAndPlan" && (
            <SubscriptionAndPlanWithAccess userId={userDetails} />
          )}
          {key === "Beneficiaries" && (
            <BeneficiaryWithAccess
              userId={userDetails}
              userStatus={detail?.status}
            />
          )}
          {key === "Transactions" && (
            <TransactionWithAccess
              userId={userDetails}
              partnerCode={detail?.partnerCode}
            />
          )}
          {key === "LienTransactions" && (
            <LienTransactionWithAccess
              userId={userDetails}
              partnerCode={detail?.partnerCode}
            />
          )}
          {key === "Documents" && <DocumentsWithAccess userId={userDetails} />}
          {key === "Transfers" && (
            <TransfersWithAccess userId={userDetails} detail={detail} userStatus={detail?.status} />
          )}
        </div>
      )}
      <CommentModal
        isOpen={isCommentModalOpen}
        onRequestClose={() => {
          setIsCommentModalOpen(false);
          setComment("");
          setError("");
          setErrorMessage("");
          setSuccessMessage("");
        }}
        comment={comment}
        setComment={setComment}
        setIsComment={setIsComment}
        error={error}
        setError={setError}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        successMessage={successMessage}
        setSuccessMessage={setSuccessMessage}
        loading={loading}
        onSave={handleConfirmCloseAccount}
        modalTitle="Close Account Comment"
      />
    </div>
  );
};

export default withFeatureActions(Mcb, "MCB", "Users");
